import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import { getPublicPricingPlans } from '../services/api';
import './Pricing.css';

const Pricing = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const tr = i18n.language?.startsWith('tr');

  useEffect(() => {
    fetchPlans();
  }, []);

  const getStaticPlans = () => [
    {
      plan_key: 'FREE',
      display_name: t('pricing.plans.free.name', 'Free'),
      price_monthly: 0,
      currency: 'TRY',
      description: t('pricing.plans.free.description', 'Start for free'),
      max_users: 1,
      max_connections: 1,
      max_devices_per_user: 1,
      bandwidth_limit_mbps: 10,
      features: [
        t('pricing.plans.free.feature1', '1 User'),
        t('pricing.plans.free.feature2', '1 Connection'),
        t('pricing.plans.free.feature3', 'Basic Support'),
      ],
      is_highlighted: false,
    },
    {
      plan_key: 'BASIC',
      display_name: t('pricing.plans.starter.name', 'Starter'),
      price_monthly: 99,
      currency: 'TRY',
      description: t('pricing.plans.starter.description'),
      max_users: 5,
      max_connections: 3,
      max_devices_per_user: 2,
      bandwidth_limit_mbps: 50,
      features: [
        t('pricing.plans.starter.feature1'),
        t('pricing.plans.starter.feature2'),
        t('pricing.plans.starter.feature3'),
        t('pricing.plans.starter.feature4'),
      ],
      is_highlighted: false,
    },
    {
      plan_key: 'PREMIUM',
      display_name: t('pricing.plans.professional.name', 'Professional'),
      price_monthly: 299,
      currency: 'TRY',
      description: t('pricing.plans.professional.description'),
      max_users: 25,
      max_connections: 10,
      max_devices_per_user: 5,
      bandwidth_limit_mbps: 0,
      features: [
        t('pricing.plans.professional.feature1'),
        t('pricing.plans.professional.feature2'),
        t('pricing.plans.professional.feature3'),
        t('pricing.plans.professional.feature4'),
        t('pricing.plans.professional.feature5'),
      ],
      is_highlighted: true,
    },
    {
      plan_key: 'ENTERPRISE',
      display_name: t('pricing.plans.enterprise.name', 'Enterprise'),
      price_monthly: 0,
      currency: 'TRY',
      description: t('pricing.plans.enterprise.description'),
      max_users: -1,
      max_connections: -1,
      max_devices_per_user: -1,
      bandwidth_limit_mbps: 0,
      features: [
        t('pricing.plans.enterprise.feature1'),
        t('pricing.plans.enterprise.feature2'),
        t('pricing.plans.enterprise.feature3'),
        t('pricing.plans.enterprise.feature4'),
      ],
      is_highlighted: false,
    },
  ];

  const fetchPlans = async () => {
    try {
      const data = await getPublicPricingPlans();
      setPlans(data?.length ? data : getStaticPlans());
    } catch (err) {
      console.error('Failed to fetch pricing plans:', err);
      setPlans(getStaticPlans());
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { TRY: '₺', USD: '$', EUR: '€' };
    return symbols[currency] || currency;
  };

  const formatLimit = (value, unlimitedLabel) => {
    if (value === -1 || value === 0) return unlimitedLabel;
    return String(value);
  };

  return (
    <PublicLayout title={t('pricing.title')} subtitle={t('pricing.subtitle')}>
      <div className="pricing-page">
        {loading ? (
          <div className="pricing-loading">
            <div className="spinner" />
            <p>{tr ? 'Planlar yükleniyor…' : 'Loading plans…'}</p>
          </div>
        ) : (
          <div className="pricing-list">
            {plans.map((plan) => {
              const isEnterprise = plan.plan_key === 'ENTERPRISE' && !plan.price_monthly;
              const isFree = plan.plan_key === 'FREE' || plan.price_monthly === 0 && plan.plan_key !== 'ENTERPRISE';
              return (
                <article
                  key={plan.plan_key}
                  className={`pricing-row ${plan.is_highlighted ? 'is-featured' : ''}`}
                >
                  <div className="pricing-row-main">
                    <div className="pricing-row-head">
                      <div>
                        {plan.is_highlighted && (
                          <span className="pricing-chip">{t('pricing.popular', 'Most Popular')}</span>
                        )}
                        <h3>{plan.display_name}</h3>
                        {plan.description && <p>{plan.description}</p>}
                      </div>
                      <div className="pricing-row-price">
                        {isEnterprise ? (
                          <strong>{t('pricing.contactUs', 'Contact Us')}</strong>
                        ) : isFree && plan.plan_key === 'FREE' ? (
                          <strong>{tr ? 'Ücretsiz' : 'Free'}</strong>
                        ) : (
                          <>
                            <strong>
                              {getCurrencySymbol(plan.currency)}
                              {plan.price_monthly}
                            </strong>
                            <span>/{t('pricing.month', 'month')}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="pricing-meta">
                      <span>
                        {formatLimit(plan.max_users, tr ? 'Sınırsız' : 'Unlimited')}{' '}
                        {tr ? 'kullanıcı' : 'users'}
                      </span>
                      <span>
                        {formatLimit(plan.max_connections, tr ? 'Sınırsız' : 'Unlimited')}{' '}
                        {tr ? 'bağlantı' : 'connections'}
                      </span>
                      <span>
                        {plan.bandwidth_limit_mbps === 0
                          ? tr
                            ? 'Sınırsız bant'
                            : 'Unlimited bandwidth'
                          : `${plan.bandwidth_limit_mbps} Mbps`}
                      </span>
                      <span>
                        {formatLimit(plan.max_devices_per_user, tr ? 'Sınırsız' : 'Unlimited')}{' '}
                        {tr ? 'cihaz/kullanıcı' : 'devices/user'}
                      </span>
                    </div>

                    <ul className="pricing-points">
                      {(plan.features || []).slice(0, 5).map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="pricing-row-action">
                    <button
                      type="button"
                      className={`arc-btn ${plan.is_highlighted ? 'arc-btn-solid' : 'arc-btn-ghost'}`}
                      onClick={() => navigate(isEnterprise ? '/contact' : '/signup')}
                    >
                      {isEnterprise
                        ? t('pricing.contactBtn', 'Contact Sales')
                        : plan.plan_key === 'FREE'
                          ? t('pricing.freeBtn', 'Start Free')
                          : t('pricing.startBtn', 'Get Started')}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <section className="pricing-faq">
          <h2>{t('pricing.faq.title')}</h2>
          <div className="faq-grid">
            {[1, 2, 3, 4].map((n) => (
              <article className="faq-item" key={n}>
                <h4>{t(`pricing.faq.q${n}`)}</h4>
                <p>{t(`pricing.faq.a${n}`)}</p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </PublicLayout>
  );
};

export default Pricing;
